import { useQuery } from '@tanstack/react-query';
import type { MediaAsset, Screen } from '@ghostscreen/contracts';
import { api } from '../../lib/api';
import { useOrganisationId } from '../../lib/session';
import type { DisplayPreviewSlide } from './DisplayPreviewCarousel';
import { resolveDisplayPreviewSrc } from './resolveDisplayPreviewSrc';

function kindFromMime(mimeType: string): 'image' | 'video' {
  return mimeType.startsWith('video/') ? 'video' : 'image';
}

type PreviewSlidesOptions = {
  /** When set, preview this playlist instead of the screen's assigned one. */
  playlistIdOverride?: string | null;
};

/**
 * Resolves playlist media into carousel slides.
 * Draft item order wins so rearranging a playlist updates display cards immediately;
 * live published URLs fill in when a library preview is missing.
 */
export function useDisplayPreviewSlides(
  screen: Screen,
  options?: PreviewSlidesOptions,
): {
  slides: DisplayPreviewSlide[];
  isPending: boolean;
} {
  const organisationId = useOrganisationId();

  const assignmentQuery = useQuery({
    queryKey: ['screen-assignment', organisationId, screen.id],
    queryFn: () => api.getScreenAssignment(organisationId, screen.id),
    enabled: options?.playlistIdOverride === undefined,
  });

  const playlistId =
    options?.playlistIdOverride !== undefined
      ? options.playlistIdOverride
      : (assignmentQuery.data?.playlistId ?? null);
  const desiredVersion = screen.desiredPlaylistVersion;

  // Shared with PlaylistEditor so draft reorder / add / remove refresh this card.
  const itemsQuery = useQuery({
    queryKey: ['playlist-items', playlistId],
    queryFn: () => api.listPlaylistItems(organisationId, playlistId!),
    enabled: Boolean(playlistId),
  });

  const liveQuery = useQuery({
    queryKey: ['playlist-version', playlistId, desiredVersion],
    queryFn: () => api.getPlaylistVersion(organisationId, playlistId!, desiredVersion!),
    enabled: Boolean(playlistId && desiredVersion && desiredVersion > 0),
  });

  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
  });

  const mediaById = new Map<string, MediaAsset>(
    (mediaQuery.data ?? []).map((asset) => [asset.id, asset]),
  );

  const liveByAssetId = new Map(
    (liveQuery.data?.items ?? []).map((item) => [item.assetId, item]),
  );

  const slides: DisplayPreviewSlide[] = [];
  const draftItems = itemsQuery.data ?? [];

  if (draftItems.length > 0) {
    for (const item of draftItems) {
      const asset = mediaById.get(item.mediaAssetId);
      const live = liveByAssetId.get(item.mediaAssetId);
      const kind = asset?.kind ?? (live ? kindFromMime(live.mimeType) : 'image');
      const src = resolveDisplayPreviewSrc({
        kind,
        asset,
        publishedUrl: live?.url,
      });
      if (!src) continue;
      slides.push({
        id: `${item.id}-${item.mediaAssetId}`,
        itemId: item.id,
        src,
        kind,
        durationSeconds: item.durationSeconds,
      });
    }
  } else if (liveQuery.data?.items.length) {
    for (const item of liveQuery.data.items) {
      const asset = mediaById.get(item.assetId);
      const kind = asset?.kind ?? kindFromMime(item.mimeType);
      const src = resolveDisplayPreviewSrc({
        kind,
        asset,
        publishedUrl: item.url,
      });
      if (!src) continue;
      slides.push({
        id: `${item.assetId}-${item.sha256}`,
        src,
        kind,
        durationSeconds: item.durationSeconds,
      });
    }
  }

  return {
    slides,
    isPending:
      (options?.playlistIdOverride === undefined && assignmentQuery.isPending) ||
      (Boolean(playlistId) && itemsQuery.isPending) ||
      (Boolean(playlistId && desiredVersion && desiredVersion > 0) && liveQuery.isPending) ||
      mediaQuery.isPending,
  };
}
